import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkle, EnvelopeSimple, WarningCircle, CalendarBlank, Lightning, ArrowRight, Warning, Clock, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { fmtTime, labelConfig } from "@/lib/format";
import { toast } from "sonner";

function renderInline(text) {
  // strip stray emoji markers, render **bold**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function renderBriefing(text) {
  const lines = text.split("\n");
  const out = [];
  let list = [];
  const flush = (key) => {
    if (list.length) { out.push(<ul key={`ul-${key}`}>{list.map((l, i) => <li key={i}>{renderInline(l)}</li>)}</ul>); list = []; }
  };
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flush(i); return; }
    if (line.startsWith("## ")) { flush(i); out.push(<h2 key={i}>{line.slice(3)}</h2>); }
    else if (/^[-*]\s/.test(line)) { list.push(line.replace(/^[-*]\s/, "")); }
    else if (/^\d+\.\s/.test(line)) { list.push(line.replace(/^\d+\.\s/, "")); }
    else { flush(i); out.push(<p key={i}>{renderInline(line.replace(/^#+\s*/, ""))}</p>); }
  });
  flush("end");
  return out;
}

const STAT = [
  { key: "unread", label: "Unread", icon: EnvelopeSimple, tint: "text-slate-700 bg-slate-100" },
  { key: "urgent", label: "Urgent", icon: WarningCircle, tint: "text-rose-700 bg-rose-100" },
  { key: "today_events", label: "Today's events", icon: CalendarBlank, tint: "text-indigo-700 bg-indigo-100" },
  { key: "pending_actions", label: "Pending actions", icon: Lightning, tint: "text-amber-700 bg-amber-100" },
];

export default function Briefing({ user }) {
  const qc = useQueryClient();
  const { data: stats } = useQuery({ queryKey: ["dashboard"], queryFn: api.dashboard });
  const { data: briefing } = useQuery({ queryKey: ["briefing"], queryFn: api.briefing });
  const { data: events } = useQuery({ queryKey: ["calendar"], queryFn: api.calendar });
  const { data: actions } = useQuery({ queryKey: ["actions"], queryFn: api.actions });

  const gen = useMutation({
    mutationFn: api.generateBriefing,
    onSuccess: (d) => { qc.setQueryData(["briefing"], d); toast.success("Briefing ready"); },
    onError: () => toast.error("Could not generate briefing"),
  });

  const emailBriefing = useMutation({
    mutationFn: () => api.notify(
      "Your daily briefing from Auppo",
      `<div style="font-family:Auppol,sans-serif;color:#334155;line-height:1.6"><h2 style="color:#0f172a">Your daily briefing</h2><pre style="white-space:pre-wrap;font-family:Auppol,sans-serif">${(briefing?.content || "").replace(/[<>]/g, "")}</pre></div>`
    ),
    onSuccess: () => toast.success("Briefing emailed to you"),
    onError: (e) => toast.error(e?.response?.data?.detail || "Email not configured — add RESEND_API_KEY"),
  });

  const today = new Date().toISOString().slice(0, 10);
  const todayEvents = useMemo(
    () => (events || []).filter((e) => e.start.slice(0, 10) === today).sort((a, b) => a.start.localeCompare(b.start)),
    [events, today]
  );
  const firstName = user?.name?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
      <PageHeader
        testid="briefing-header"
        eyebrow={new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        title={`${greeting}, ${firstName}`}
        subtitle="Here's your command center for today."
        actions={
          <>
            {briefing?.content && (
              <Button
                data-testid="email-briefing-btn"
                onClick={() => emailBriefing.mutate()}
                disabled={emailBriefing.isPending}
                variant="outline"
                className="rounded-xl gap-2"
              >
                <PaperPlaneTilt weight="bold" size={16} />
                Email me this
              </Button>
            )}
            <Button
              data-testid="generate-briefing-btn"
              onClick={() => gen.mutate()}
              disabled={gen.isPending}
              className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2"
            >
              <Sparkle weight="fill" size={16} />
              {gen.isPending ? "Thinking…" : briefing?.content ? "Regenerate briefing" : "Generate briefing"}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {STAT.map(({ key, label, icon: Icon, tint }, i) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            data-testid={`stat-${key}`}
            className="bg-white border border-border rounded-2xl p-5"
          >
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${tint}`}>
              <Icon size={18} weight="bold" />
            </div>
            <div className="text-3xl font-heading font-light text-slate-900">{stats?.[key] ?? 0}</div>
            <div className="text-sm text-slate-500 mt-0.5">{label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-2xl p-7">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-7 w-7 rounded-lg bg-slate-900 flex items-center justify-center">
              <Sparkle weight="fill" size={14} className="text-white" />
            </div>
            <h3 className="font-heading font-semibold text-lg">Your daily briefing</h3>
          </div>
          {stats?.conflicts > 0 && (
            <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5 mb-4">
              <Warning weight="fill" size={16} />
              {stats.conflicts} calendar conflict{stats.conflicts > 1 ? "s" : ""} detected today — review your Calendar.
            </div>
          )}
          {gen.isPending ? (
            <div className="space-y-2.5 aria-thinking">
              {[...Array(5)].map((_, i) => <div key={i} className="h-3.5 bg-slate-100 rounded" style={{ width: `${90 - i * 8}%` }} />)}
            </div>
          ) : briefing?.content ? (
            <div className="briefing-md" data-testid="briefing-content">{renderBriefing(briefing.content)}</div>
          ) : (
            <div className="text-center py-10">
              <p className="text-slate-500 mb-4">No briefing yet. Let Auppo summarize your day.</p>
              <Button data-testid="empty-generate-briefing-btn" onClick={() => gen.mutate()} className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2">
                <Sparkle weight="fill" size={16} /> Generate now
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Today's agenda</h3>
              <Link to="/calendar" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                View all <ArrowRight size={12} />
              </Link>
            </div>
            {todayEvents.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Nothing scheduled today.</p>
            ) : (
              <div className="space-y-3">
                {todayEvents.map((e) => (
                  <div key={e.id} data-testid={`agenda-item-${e.id}`} className="flex gap-3">
                    <div className={`w-1 rounded-full ${e.is_conflict ? "bg-amber-500" : "bg-slate-300"}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">
                        {e.title}
                        {e.is_conflict && <Warning weight="fill" size={13} className="text-amber-500 shrink-0" />}
                      </div>
                      <div className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock size={11} /> {fmtTime(e.start)} – {fmtTime(e.end)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Pending actions</h3>
              <Link to="/actions" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">
                Review <ArrowRight size={12} />
              </Link>
            </div>
            {(actions?.length || 0) === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Nothing awaiting your approval.</p>
            ) : (
              <div className="space-y-2.5">
                {actions.slice(0, 4).map((a) => (
                  <Link key={a.id} to="/actions" className="flex items-center gap-2.5 group">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0" />
                    <span className="text-sm text-slate-700 truncate group-hover:text-slate-900">{a.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
